import { CALLING_CODES } from '@/lib/countries.generated';

/**
 * What a parent typed into "Mobile number or email": an email, a phone number in any country, or
 * not enough to tell yet. The server has the final word (E.164 and email checks); this only decides
 * where the code goes and gives an early, plain message.
 */
export type Parsed =
  | { kind: 'email'; value: string }
  | { kind: 'phone'; e164: string; country: string; national: string }
  | { kind: 'invalid'; reason: 'empty' | 'email' | 'phone' | 'phoneIndia' };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INDIA_MOBILE = /^[6-9]\d{9}$/;

/** True while the text could still be a phone number: digits, spaces, + - ( ) . and nothing else. */
export const looksLikePhone = (raw: string): boolean => /^[\d\s+\-().]*$/.test(raw) && !raw.includes('@');

const CODE_TO_COUNTRIES = Object.entries(CALLING_CODES).reduce<Record<string, string[]>>((acc, [country, code]) => {
  (acc[code] ??= []).push(country);
  return acc;
}, {});

/**
 * `+447700900123` → { country: 'GB', national: '7700900123' }. Calling codes never prefix each
 * other, so the first 1–3 digits that are a code are the code. Where a code is shared (+1, +44, +7…)
 * the country the parent picked wins if it shares it, else the main country for that code.
 */
export const splitInternational = (digits: string, preferred: string): { country: string; code: string; national: string } | undefined => {
  for (const length of [1, 2, 3]) {
    const code = digits.slice(0, length);
    const countries = CODE_TO_COUNTRIES[code];
    if (!countries) continue;
    const main = { '1': 'US', '7': 'RU', '44': 'GB', '61': 'AU', '39': 'IT', '47': 'NO', '590': 'GP', '262': 'RE', '358': 'FI', '599': 'CW', '212': 'MA' }[code];
    const country = countries.includes(preferred) ? preferred : (main ?? countries[0]!);
    return { country, code, national: digits.slice(length) };
  }
  return undefined;
};

/** E.164: 8 to 15 digits with the country code (the server checks the same). */
const nationalOk = (national: string, code: string) =>
  national.length >= 4 && national.length + code.length >= 8 && national.length + code.length <= 15;

/**
 * A phone number typed with or without its country code, for the country shown in the picker.
 * India: a 10-digit mobile starting 6–9, also accepted with a leading 0 or 91 (`09876543210`,
 * `91 98765 43210`). Elsewhere: the national number without its trunk 0.
 */
export const parsePhone = (raw: string, country: string): Parsed => {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return { kind: 'invalid', reason: 'empty' };

  let target = country;
  let national: string;
  if (trimmed.startsWith('+') || trimmed.startsWith('00')) {
    const split = splitInternational(trimmed.startsWith('00') ? digits.slice(2) : digits, country);
    if (!split) return { kind: 'invalid', reason: 'phone' };
    target = split.country;
    national = split.national.replace(/^0+/, '');
  } else if (country === 'IN') {
    national = digits.replace(/^0+/, '');
    if (national.length === 12 && national.startsWith('91')) national = national.slice(2);
  } else {
    national = digits.replace(/^0+/, '');
  }

  const code = CALLING_CODES[target] ?? '91';
  if (target === 'IN') {
    return INDIA_MOBILE.test(national)
      ? { kind: 'phone', e164: `+91${national}`, country: 'IN', national }
      : { kind: 'invalid', reason: 'phoneIndia' };
  }
  return nationalOk(national, code)
    ? { kind: 'phone', e164: `+${code}${national}`, country: target, national }
    : { kind: 'invalid', reason: 'phone' };
};

export const parseEmail = (raw: string): Parsed => {
  const value = raw.trim().toLowerCase();
  if (!value) return { kind: 'invalid', reason: 'empty' };
  return EMAIL_PATTERN.test(value) ? { kind: 'email', value } : { kind: 'invalid', reason: 'email' };
};

/** The combined "Mobile number or email" field. */
export const parseIdentifier = (raw: string, country: string): Parsed => {
  if (!raw.trim()) return { kind: 'invalid', reason: 'empty' };
  return looksLikePhone(raw) ? parsePhone(raw, country) : parseEmail(raw);
};

/** `+919876543210` → `+91 98••• ••210`; other numbers keep their first two and last three digits. */
export const maskPhone = (e164: string, country: string): string => {
  const code = CALLING_CODES[country] ?? '';
  const national = e164.replace(/^\+/, '').slice(code.length);
  return `+${code} ${national.slice(0, 2)}••• ••${national.slice(-3)}`;
};

/** 🇮🇳 from `IN`. */
export const flagOf = (country: string): string =>
  /^[A-Z]{2}$/.test(country) ? String.fromCodePoint(...[...country].map((c) => 0x1f1a5 + c.charCodeAt(0))) : '';
